import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Phone, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BUSINESS } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "My PC is running very slow",
  "I think I have a virus",
  "My Mac won't boot",
  "Recommend an antivirus",
];

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function LiveChat() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(true);
  const [step, setStep] = useState("intro"); // intro | chat
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => uid());
  const [messages, setMessages] = useState([
    {
      from: "agent",
      text: "Hi! 👋 You're chatting with the Repair Point team. Tell us briefly what's wrong and a technician will reply within a few minutes during opening hours.",
      ts: Date.now(),
    },
  ]);

  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open, step]);

  const startChat = () => {
    if (name.trim().length < 2) return;
    setStep("chat");
    setMessages((m) => [
      ...m,
      { from: "agent", text: `Thanks ${name.trim().split(" ")[0]} — what can we help you with today?`, ts: Date.now() },
    ]);
  };

  const sendMessage = async (text) => {
    const body = (text ?? draft).trim();
    if (!body || sending) return;
    setSending(true);
    setMessages((m) => [...m, { from: "user", text: body, ts: Date.now() }]);
    setDraft("");
    try {
      await axios.post(`${API}/chat/messages`, {
        session_id: sessionId,
        name: name || "Anonymous",
        email: email || null,
        message: body,
      });
      setTimeout(() => {
        setMessages((m) => [
          ...m,
          {
            from: "agent",
            text: "Got it — a technician has been pinged. They will reply here or call you back shortly. Outside hours? Email us and we'll respond first thing.",
            ts: Date.now(),
            ack: true,
          },
        ]);
      }, 450);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          from: "agent",
          text: "We couldn't deliver that message just now. Please call or email us directly and we'll respond immediately.",
          ts: Date.now(),
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div data-testid="live-chat-widget" className="fixed bottom-5 left-5 md:bottom-6 md:left-6 z-[60] flex flex-col items-start gap-3">
      {open && (
        <div
          data-testid="live-chat-panel"
          className="w-[92vw] max-w-sm h-[520px] max-h-[80vh] bg-card border border-border rounded-md shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4"
        >
          {/* Header */}
          <div className="bg-primary text-primary-foreground px-5 py-4 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative h-10 w-10 rounded-full bg-primary-foreground/10 flex items-center justify-center shrink-0">
                <MessageCircle className="h-5 w-5" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-accent ring-2 ring-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-heading font-semibold text-base leading-tight">Repair Point Live</p>
                <p className="text-xs text-primary-foreground/75 leading-tight">Usually replies in a few minutes</p>
              </div>
            </div>
            <button
              data-testid="chat-close"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-primary-foreground/10 transition-colors shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 bg-secondary/40 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3.5 py-2.5 rounded-md text-sm leading-relaxed ${
                    m.from === "user"
                      ? "bg-primary text-primary-foreground rounded-br-none"
                      : "bg-card border border-border text-foreground rounded-bl-none"
                  }`}
                >
                  {m.ack && <CheckCircle2 className="h-3.5 w-3.5 text-accent inline mr-1 -mt-0.5" />}
                  {m.text}
                </div>
              </div>
            ))}

            {step === "chat" && messages.length <= 2 && (
              <div className="pt-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="text-xs px-3 py-1.5 rounded-full bg-card border border-border hover:border-primary hover:text-primary transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Intake or input */}
          {step === "intro" ? (
            <div className="border-t border-border p-4 bg-card space-y-3">
              <p className="text-xs text-muted-foreground">Tell us your name to start the chat (no signup).</p>
              <Input
                data-testid="chat-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                onKeyDown={(e) => e.key === "Enter" && startChat()}
              />
              <Input
                data-testid="chat-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional, so we can follow up)"
              />
              <Button
                data-testid="chat-start-btn"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={startChat}
                disabled={name.trim().length < 2}
              >
                Start chat
              </Button>
              <div className="flex items-center justify-center gap-4 pt-1">
                <a href={BUSINESS.phoneHref} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-primary">
                  <Phone className="h-3 w-3" /> Call
                </a>
                <a href={BUSINESS.emailHref} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-primary">
                  <Mail className="h-3 w-3" /> Email
                </a>
              </div>
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="border-t border-border p-3 bg-card flex items-center gap-2"
            >
              <Input
                data-testid="chat-message-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Type a message…"
                className="flex-1"
                disabled={sending}
              />
              <Button
                type="submit"
                data-testid="chat-send-btn"
                size="icon"
                className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                disabled={sending || !draft.trim()}
                aria-label="Send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          )}
        </div>
      )}

      <button
        data-testid="live-chat-toggle"
        onClick={() => { setOpen(!open); setUnread(false); }}
        aria-label={open ? "Close chat" : "Open chat"}
        className="relative h-14 w-14 md:h-auto md:w-auto md:px-5 md:py-3 rounded-full bg-primary text-primary-foreground shadow-xl hover:scale-105 active:scale-100 transition-transform inline-flex items-center justify-center gap-2 font-semibold"
      >
        {open ? (
          <X className="h-5 w-5" />
        ) : (
          <>
            <MessageCircle className="h-5 w-5" />
            <span className="hidden md:inline text-sm">Chat with us</span>
            {unread && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-accent ring-2 ring-background" />
            )}
          </>
        )}
      </button>
    </div>
  );
}
